/** Domain identity → duotone glyph mapping for {@link DomainIcon}. */
import {
  faEnvelope,
  faSparkles,
  faMemoPad,
  faBook,
  faBooks,
  faBookSparkles,
  faLampDesk,
  faUtensils,
  faAppleWhole,
  faClipboardCheck,
  faCircleDollar,
  faBookmark,
  faHouse,
  faMagnifyingGlass,
  faStar,
  faEllipsis,
} from '@fortawesome/pro-duotone-svg-icons';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * Color-bearing nav icon identities. Each maps to a `--domain-<key>` CSS token.
 * The first nine are true content domains; the last five are the remaining
 * left-nav icons (utilities like Home/Search get a quieter, muted tone — see the
 * `--domain-*` token values).
 */
export type DomainKey =
  | 'inbox'
  | 'chat'
  | 'notes'
  | 'journal'
  | 'study'
  | 'research'
  | 'meals'
  | 'food-diary'
  | 'tasks'
  | 'financial'
  | 'reading'
  | 'home'
  | 'search'
  | 'starred'
  | 'later'
  | 'more';

/** Stable ordered list of every key — handy for iteration and tests. */
export const DOMAIN_KEYS: readonly DomainKey[] = [
  'inbox',
  'chat',
  'notes',
  'journal',
  'study',
  'research',
  'meals',
  'food-diary',
  'tasks',
  'financial',
  'reading',
  'home',
  'search',
  'starred',
  'later',
  'more',
] as const;

/**
 * Duotone glyph for each key. Mirrors the current monochrome nav icons so the
 * visual language is unchanged — only the weight (duotone) and color differ.
 */
export const DOMAIN_ICONS: Record<DomainKey, IconDefinition> = {
  inbox: faEnvelope,
  chat: faSparkles,
  notes: faMemoPad,
  journal: faBook,
  study: faLampDesk,
  research: faBookSparkles,
  meals: faUtensils,
  'food-diary': faAppleWhole,
  tasks: faClipboardCheck,
  financial: faCircleDollar,
  reading: faBookmark,
  home: faHouse,
  search: faMagnifyingGlass,
  starred: faStar,
  // The Reading Room is a place you go to read, not a queue of deferred items —
  // a stack of books names the destination; the old clock named the delay.
  later: faBooks,
  more: faEllipsis,
};
